import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canManage, isCleaner } from "@/lib/perms";
import { StatusBadge, SeverityBadge } from "@/components/Badges";
import { label } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import IssueDialog from "@/components/IssueDialog";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";

const FILTERS = ["all", "open", "resolved"];

export default function Issues() {
  const { user } = useAuth();
  const [issues, setIssues] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const severity = searchParams.get("severity") || "";
  const focusId = searchParams.get("focus") || "";
  const [filter, setFilter] = useState(severity || focusId ? "all" : "open");

  const load = useCallback(() => api.get("/issues").then(({ data }) => setIssues(data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (iid) => {
    try {
      await api.put(`/issues/${iid}`, { status: "resolved" });
      toast.success("Issue resolved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const remove = async (iid) => {
    try {
      await api.delete(`/issues/${iid}`);
      toast.success("Issue deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete issue");
    }
  };

  const byStatus = filter === "all" ? issues : issues.filter((i) => i.status === filter);
  const visible = severity ? byStatus.filter((i) => i.severity === severity) : byStatus;

  return (
    <div className="space-y-6" data-testid="issues-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Property condition</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Issues</h1>
        </div>
        {<IssueDialog onCreated={load} showPropertyPicker />}
      </div>

      {severity && (
        <div className="flex items-center gap-3 rounded-lg border border-gold/40 bg-gold/10 px-4 py-2.5" data-testid="severity-filter-banner">
          <AlertTriangle className="h-4 w-4 text-gold" />
          <p className="text-sm text-royal-deep">
            Showing <span className="font-semibold">{label(severity)}</span> issues only
          </p>
          <button
            data-testid="clear-severity-filter"
            onClick={() => setSearchParams({})}
            className="ml-auto text-xs font-semibold text-royal hover:text-gold transition-colors duration-200"
          >
            Clear filter
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2" data-testid="issue-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`issue-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-4 min-h-[40px] text-xs font-medium touch-manipulation select-none transition-colors duration-200 ${
                filter === f ? "bg-royal text-white border-royal" : "bg-white text-royal border-border hover:border-royal/40"
              }`}
            >
              {label(f)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4" data-testid="issues-list">
        {visible.length === 0 && (
          <div className="rounded-lg border border-border bg-white p-10 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto" />
            <p className="font-display text-lg font-semibold text-royal-deep mt-3">Nothing to report</p>
            <p className="text-sm text-muted-foreground mt-1">No issues match this filter.</p>
          </div>
        )}
        {visible.map((i, idx) => (
          <div
            key={i._id}
            data-testid={`issue-card-${i._id}`}
            style={{ animationDelay: `${idx * 50}ms` }}
            className={`rise rounded-lg border bg-white p-5 flex flex-wrap gap-4 ${
              focusId === i._id ? "border-gold ring-2 ring-gold/30" : "border-border"
            }`}
          >
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-gold" />
                <h3 className="font-display text-base font-semibold text-royal-deep">{i.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{i.description || "No description provided."}</p>
              <p className="text-xs text-muted-foreground mt-3">
                <Link to={`/properties/${i.property_id}`} data-testid={`issue-property-link-${i._id}`} className="text-royal hover:text-gold font-medium transition-colors duration-200">
                  {i.property_name}
                </Link>
                {" · "}Reported by {i.reported_by_name}
                {i.created_at ? ` · ${new Date(i.created_at).toLocaleDateString()}` : ""}
              </p>
              <Link
                to={`/properties/${i.property_id}`}
                data-testid={`issue-open-property-${i._id}`}
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-royal hover:text-gold transition-colors duration-200"
              >
                Open property <ArrowRight className="h-3 w-3" />
              </Link>
              {i.resolution_notes && (
                <p className="text-xs text-emerald-700 mt-2">Resolution: {i.resolution_notes}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex gap-2">
                <SeverityBadge value={i.severity} testId={`issue-severity-${i._id}`} />
                <StatusBadge value={i.status} testId={`issue-status-${i._id}`} />
              </div>
              {canManage(user) && (
                <div className="flex gap-2">
                  {i.status === "open" && (
                    <Button
                      data-testid={`resolve-issue-${i._id}`}
                      onClick={() => resolve(i._id)}
                      variant="outline"
                      className="h-8 text-xs border-royal/30 text-royal hover:bg-royal/5"
                    >
                      Mark resolved
                    </Button>
                  )}
                  <Button
                    data-testid={`delete-issue-${i._id}`}
                    onClick={() => remove(i._id)}
                    variant="outline"
                    className="h-8 text-xs border-destructive/40 text-destructive hover:bg-destructive/5"
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
